import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { toast } from "sonner";

interface CreateCategoryData {
  name: string;
  description?: string;
  image_url?: string;
  gym_id: string;
}

export const useCreateReservationCategory = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();

  return useMutation({
    mutationFn: async (data: CreateCategoryData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !profile) throw new Error("Usuario no autenticado");

      // Verify manager has access to this gym
      const { data: gymManager } = await supabase
        .from('gym_managers')
        .select('*')
        .eq('manager_id', user.id)
        .eq('gym_id', data.gym_id)
        .single();

      if (!gymManager && profile.role !== 'admin') {
        throw new Error("No tienes permisos para gestionar este gimnasio");
      }

      const { data: category, error } = await supabase
        .from('reservation_categories')
        .insert({
          ...data,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return category;
    },
    onSuccess: (category) => {
      console.log('[useCreateReservationCategory] Category created successfully:', {
        categoryId: category.id,
        categoryName: category.name,
        gymId: category.gym_id
      });
      
      // Invalidate with exact query key that useReservationCategories uses
      queryClient.invalidateQueries({ 
        queryKey: ['reservation-categories', category.gym_id] 
      });
      
      // Also invalidate any queries that might use null/undefined as gymId
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          return query.queryKey[0] === 'reservation-categories';
        }
      });
      
      console.log('[useCreateReservationCategory] Cache invalidated for gym:', category.gym_id);
      toast.success("Categoría creada exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear la categoría");
    },
  });
};